<template>
  <div class="relative min-h-24">
    <div class="p-5 flex justify-stretch" v-if="payment.data !== undefined">
      <div class="w-5/12">
        <billing-data class="" :data="refill.amounts"></billing-data>
        <div
          v-if="refill.amounts && refill.amounts.amount_alert"
          class="border-orange-200 bg-orange-100 text-orange-800 -mx-5 p-5"
          v-html="refill.amounts.amount_alert"
        ></div>
        <div class="pt-5">
          <div
            class="font-bold pb-2"
            v-text="i18n.billing.billing_contact"
          ></div>
          <div class="text-gray-700 text-sm">
            <div v-text="name"></div>
            <div v-text="company" v-if="isCompany"></div>
            <div v-text="vat"></div>
            <div v-text="street"></div>
            <div>
              <span v-text="zipcode"></span> <span v-text="city"></span>
            </div>
            <div v-text="state"></div>
            <div v-text="country"></div>
            <div v-if="refill.form.external_ref !== ''">
              Ref: <span v-text="refill.form.external_ref"></span>
            </div>
          </div>
        </div>
      </div>
      <div class="pl-5 w-7/12" v-if="payment.done">
        <alert level="success" :message="payment.message" class="p-2"></alert>
      </div>
      <div class="pl-5 w-7/12" v-if="!payment.done">
        <div class="border-t border-l border-r" v-if="payment.data.stripe">
          <header
            class="p-2 bg-gray-100"
            :class="
              payment.gateway === 'stripe'
                ? 'font-bold bg-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 cursor-pointer'
            "
            @click="payment.gateway = 'stripe'"
          >
            <span class="mr-4"
              ><font-awesome-icon
                :icon="['fas', 'credit-card']"
              ></font-awesome-icon
            ></span>
            <span v-text="i18n.payment.stripe.mode"></span>
          </header>
          <main class="" v-show="payment.gateway === 'stripe'">
            <stripe
              :config="payment.data.stripe"
              :saved-payment-method="payment.data.saved_payment_method"
              :i18n="i18n"
              @payment-success="paymentSuccess"
              @reload-payment-data="getPaymentData"
              v-if="!payment.done"
            />
          </main>
        </div>

        <div class="border-t border-l border-r">
          <header
            class="p-2"
            :class="
              payment.gateway === 'transfer'
                ? 'font-bold bg-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 cursor-pointer'
            "
            @click="payment.gateway = 'transfer'"
          >
            <span class="mr-4"
              ><font-awesome-icon icon="exchange-alt"></font-awesome-icon
            ></span>
            <span v-text="i18n.payment.transfer.mode"></span>
          </header>
          <main v-show="payment.gateway === 'transfer'">
            <div class="p-5" v-if="hasProof">
              <p v-text="i18n.payment.transfer.already_sent"></p>
            </div>
            <send-proof
              :refill="refill"
              :i18n="i18n"
              @proof-sent="paymentSuccess('transfer')"
            ></send-proof>
          </main>
        </div>

        <div class="border-t border-l border-r" v-if="payment.data.braintree">
          <header
            class="p-2 bg-gray-100"
            :class="
              payment.gateway === 'paypal'
                ? 'font-bold bg-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 cursor-pointer'
            "
            @click="payment.gateway = 'paypal'"
          >
            <span class="ml-1 mr-4"
              ><font-awesome-icon :icon="['fab', 'paypal']"></font-awesome-icon
            ></span>
            <span v-text="i18n.payment.paypal.mode"></span>
          </header>
          <main class="" v-show="payment.gateway === 'paypal'">
            <paypal
              :config="payment.data.braintree"
              :i18n="i18n.payment.paypal"
              @payment-success="paymentSuccess"
              v-if="!payment.done"
            ></paypal>
          </main>
        </div>

        <div class="border">
          <header
            class="p-2 bg-gray-100"
            :class="
              payment.gateway === 'third_party'
                ? 'font-bold bg-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 cursor-pointer'
            "
            @click="payment.gateway = 'third_party'"
          >
            <span class="mr-4"
              ><font-awesome-icon
                :icon="['fas', 'share-alt']"
              ></font-awesome-icon
            ></span>
            <span v-text="i18n.payment.third_party.mode"></span>
          </header>
          <main class="p-5" v-show="payment.gateway === 'third_party'">
            <p class="pb-2" v-text="i18n.payment.third_party.detail"></p>
            <text-copier :message="payment.data.third_party"></text-copier>
          </main>
        </div>
      </div>
    </div>
    <saving-overlay
      opacity="0"
      text-color="text-gray-700"
      v-else
    ></saving-overlay>
  </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js/pure";

import axios from "axios";
import { handleAxiosError } from "@/boot/functions";

import BillingData from "./BillingData";
import Stripe from "../Commons/Billing/Stripe";
import TextCopier from "../Design/TextCopier";
import Alert from "../Design/Alert";
import SendProof from "./SendProof";
import Paypal from "../Commons/Billing/Paypal";

export default {
  props: ["refill"],
  components: {
    BillingData,
    Stripe,
    Paypal,
    TextCopier,
    Alert,
    SendProof,
  },
  mixins: [],
  data: function () {
    return {
      items: this.refill.files,
      payment: {
        done: false,
        gateway: "stripe",
        message: "",
        data: undefined,
      },
    };
  },
  methods: {
    getPaymentData() {
      axios
        .get("/api/app/refills/" + this.refill.id + "/payments")
        .then(({ data }) => {
          this.payment.data = data.data.payment;
          if (this.payment.data.stripe) {
            return loadStripe(this.payment.data.stripe.key);
          }
        })
        .catch(handleAxiosError);
    },

    paymentSuccess(gateway) {
      this.payment.done = true;
      this.payment.gateway = gateway;
      this.payment.message =
        this.i18n.payment[gateway].sent || this.i18n.payment.success;
      this.$emit("payment-success", this.refill.id);
    },
  },
  computed: {
    i18n() {
      if (this.$store.state.safe.i18n !== undefined) {
        return this.$store.state.safe.i18n.new_refill;
      }

      return undefined;
    },
    name() {
      return this.refill.form.firstname + " " + this.refill.form.lastname;
    },
    company() {
      return this.refill.form.company;
    },
    vat() {
      return this.refill.form.vat;
    },
    street() {
      return this.refill.form.add_main;
    },
    zipcode() {
      return this.refill.form.add_zip;
    },
    city() {
      return this.refill.form.add_city;
    },
    state() {
      return this.refill.form.add_state;
    },
    country() {
      return this.refill.form.add_country.name;
    },
    isCompany() {
      return this.refill.form.invoice_to == 1;
    },
    hasProof() {
      return this.refill.files.length > 0;
    },
  },
  mounted() {
    this.getPaymentData();
  },
};
</script>
